import type { DashboardOrder, OrderDetails } from '../model'
import { dayjs } from '@/shared/lib/dayjs'

/**
 * Returns the arrival time for an order.
 * @param lastTransitionTime - The last transition time string
 * @param eta - Optional ETA in seconds
 * @param lastUpdateTime - The last update time string (used when useLastUpdateTimeForEta is true)
 * @param useLastUpdateTimeForEta - If true and eta is present, uses lastUpdateTime + eta (for OnTheWay orders).
 *                                   If false, uses lastTransitionTime for stable sorting (for Arrived orders).
 *                                   Default: false for stable sorting.
 */
export function getArrivalTime(
  lastTransitionTime: string | undefined,
  eta?: number,
  lastUpdateTime?: string,
  useLastUpdateTimeForEta: boolean = false,
): dayjs.Dayjs {
  // If eta is provided and we should use lastUpdateTime for ETA calculations
  if (eta !== undefined && useLastUpdateTimeForEta) {
    // Use lastUpdateTime + eta (for OnTheWay orders with ETAs)
    // Fallback to lastTransitionTime if lastUpdateTime is not available
    const base = lastUpdateTime ? dayjs(lastUpdateTime) : dayjs(lastTransitionTime)
    return base.add(eta, 'second')
  }

  // For stable sorting (Arrived orders) or orders without ETA:
  // Use lastTransitionTime to ensure acknowledged orders maintain position
  const base = dayjs(lastTransitionTime)

  // If eta is provided but we're in stable mode, add it to lastTransitionTime
  if (eta !== undefined) {
    return base.add(eta, 'second')
  }

  // No eta, use lastTransitionTime directly
  return base
}/**
  * Returns the approximate arrival time for a DashboardOrder.
  * For single orders, calculates arrival time based on eta and the specified mode.
  * For group orders, returns the earliest arrival time among the group's orders.
  * @param order - The DashboardOrder to evaluate
  * @param useLastUpdateTimeForEta - If true, uses lastUpdateTime + eta for ETA calculations (OnTheWay).
  *                                   If false, uses lastTransitionTime for stable sorting (Arrived).
  */
export function handleArrivalTime(order: DashboardOrder, useLastUpdateTimeForEta: boolean = false): dayjs.Dayjs {
  if (order.type === 'single') {
    const { lastTransitionTime, eta, lastUpdateTime } = order.data
    return getArrivalTime(lastTransitionTime, eta, lastUpdateTime, useLastUpdateTimeForEta)
  } else if (order.type === 'group') {
    // For group, use the earliest calculated arrival time among all orders
    const times = order.data.orders.map(o =>
      getArrivalTime(o.lastTransitionTime, o.eta, o.lastUpdateTime, useLastUpdateTimeForEta),
    )
    return times.reduce((min, t) => (t.isBefore(min) ? t : min), times[0])
  }
  return dayjs()
}

/**
 * Sorts DashboardOrders by their stable arrival time, ensuring acknowledged orders maintain position.
 * Uses lastTransitionTime for stable sorting - specifically designed for the Arrived column
 * where acknowledgement updates lastUpdateTime but should not change order position.
 * @param orders - Array of DashboardOrder objects
 * @param order - 'asc' for earliest first, 'desc' for latest first (default: 'asc')
 */
export function sortOrdersByArrivalTime(
  orders: DashboardOrder[],
  order: 'asc' | 'desc' = 'asc',
): DashboardOrder[] {
  const direction = order === 'desc' ? -1 : 1

  return [...orders].sort((a, b) => {
    const t1 = handleArrivalTime(a, false) // false = use stable sorting with lastTransitionTime
    const t2 = handleArrivalTime(b, false)
    return (t1.valueOf() - t2.valueOf()) * direction
  })
}

/**
 * Sorts OrderDetails by their stable arrival time, ensuring acknowledged orders maintain position.
 * Uses lastTransitionTime for stable sorting - specifically designed for the Arrived column.
 * @param orders - Array of OrderDetails objects
 * @param order - 'asc' for earliest first, 'desc' for latest first (default: 'asc')
 */
export function sortOrderDetailsByArrivalTime(
  orders: OrderDetails[],
  order: 'asc' | 'desc' = 'asc',
): OrderDetails[] {
  const direction = order === 'desc' ? -1 : 1
  return [...orders].sort((a, b) => {
    const t1 = getArrivalTime(a.lastTransitionTime, a.eta, a.lastUpdateTime, false) // false = use stable sorting
    const t2 = getArrivalTime(b.lastTransitionTime, b.eta, b.lastUpdateTime, false)
    return (t1.valueOf() - t2.valueOf()) * direction
  })
}

/**
 * Checks if a DashboardOrder is in an error state or has an ETA set.
 * @param order - The DashboardOrder to check
 * @param errorStates - Array of error status strings
 */
export function stateIsErrorOrHasEta(order: DashboardOrder, errorStates: string[]): boolean {
  if (order.type === 'single') {
    return errorStates.includes(order.data.status) || order.data.eta !== undefined
  } else if (order.type === 'group') {
    return order.data.orders.some(o => errorStates.includes(o.status) || o.eta !== undefined)
  }
  return false
}

/**
 * Creates a sort function for DashboardOrders that implements the dashboard's business sorting rules.
 *
 * Business Logic:
 * 1. **Priority Grouping**: Orders with an ETA or in an error state (as defined by `errorStates`) are grouped at the top. All other orders are grouped below.
 * 2. **ETA Value Sorting**: Orders with ETAs are sorted by their ETA value (lowest ETA first). For group orders, the minimum ETA among all orders in the group is used.
 * 3. **ETA vs Non-ETA**: Orders with ETAs are prioritized over orders without ETAs.
 * 4. **ETA Direction**: For orders without ETA values, they are sorted by `etaDirection` - `lessThan` first, then `undefined`, then `greaterThan`.
 * 5. **Arrival Time**: Within each group, orders are sorted by their "approximate arrival time" (earliest first).
 *
 * This ensures that orders with the shortest ETAs appear first, making the most urgent deliveries immediately visible.
 *
 * @param errorStates - Array of error status strings (e.g., ['OVERDUE', 'ARRIVED'])
 * @returns A comparison function for sorting DashboardOrders
 */
export function createDashboardOrderSortFn(errorStates: string[]) {
  return (order1: DashboardOrder, order2: DashboardOrder) => {
    if (!order1 || !order2)
      return 0

    // Priority group: error/ETA at top
    const group1 = stateIsErrorOrHasEta(order1, errorStates)
    const group2 = stateIsErrorOrHasEta(order2, errorStates)
    if (group1 && !group2)
      return -1
    if (!group1 && group2)
      return 1

    // Helper function to get etaDirection - for groups, use the order with the earliest ETA
    const getEtaDirection = (o: DashboardOrder) => {
      if (o.type === 'single') {
        return o.data.etaDirection
      } else if (o.type === 'group') {
        // For group orders, find the order with the earliest ETA
        const ordersWithEta = o.data.orders.filter(order => order.eta !== undefined && order.etaDirection)
        if (ordersWithEta.length === 0)
          return undefined

        // Find the order with the minimum ETA
        const earliestEtaOrder = ordersWithEta.reduce((min, order) =>
          (order.eta! < min.eta!) ? order : min,
        )
        return earliestEtaOrder.etaDirection
      }
      return undefined
    }

    // Helper function to get the earliest ETA value for sorting
    const getEarliestEta = (o: DashboardOrder): number | undefined => {
      if (o.type === 'single') {
        return o.data.eta
      } else if (o.type === 'group') {
        const ordersWithEta = o.data.orders.filter(order => order.eta !== undefined)
        if (ordersWithEta.length === 0)
          return undefined
        return Math.min(...ordersWithEta.map(order => order.eta!))
      }
      return undefined
    }

    const dir1 = getEtaDirection(order1)
    const dir2 = getEtaDirection(order2)
    const eta1 = getEarliestEta(order1)
    const eta2 = getEarliestEta(order2)

    // If both orders have ETAs, sort by ETA value first (lowest ETA first)
    if (eta1 !== undefined && eta2 !== undefined) {
      const etaDiff = eta1 - eta2
      if (etaDiff !== 0) {
        return etaDiff
      }
      // ETAs are equal, fall through to etaDirection comparison
    }

    // If only one has ETA, prioritize the one with ETA
    if (eta1 !== undefined && eta2 === undefined)
      return -1
    if (eta1 === undefined && eta2 !== undefined)
      return 1

    // If neither has ETA, sort by etaDirection: lessThan < undefined < greaterThan
    if (dir1 !== dir2) {
      if (dir1 === 'lessThan')
        return -1
      if (dir2 === 'lessThan')
        return 1
      if (dir1 === 'greaterThan')
        return 1
      if (dir2 === 'greaterThan')
        return -1
    }

    // Then sort by arrival time (ascending)
    // Use lastUpdateTime for ETA calculations in OnTheWay column
    const arrivalTime1 = handleArrivalTime(order1, true)
    const arrivalTime2 = handleArrivalTime(order2, true)
    return arrivalTime1.valueOf() - arrivalTime2.valueOf()
  }
}

/**
 * Sorts DashboardOrders using the dashboard's business rules (for OnTheWay column):
 * Uses lastUpdateTime + eta for ETA calculations, which provides accurate time until arrival.
 *
 * This function is the main entry point for dashboard order sorting and should be used wherever dashboard order lists are displayed.
 *
 * @param orders - Array of DashboardOrder objects
 * @param errorStates - Array of error status strings (e.g., ['OVERDUE', 'ARRIVED'])
 */
export function sortOrdersByDashboardRules(orders: DashboardOrder[], errorStates: string[]): DashboardOrder[] {
  const sortFn = createDashboardOrderSortFn(errorStates)
  return [...orders].sort(sortFn)
}
